import { Pagination } from '@talentmesh/utils';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ClientFilters, ClientFormValues } from '../components/Client';
import { useClientsOverview, useCreateClient } from './useClientsOverview';
import { useNotificationContext } from '@/Context/NotificationContext';
import { ClientOverviewResponse } from '@/DTO/Clients/ClientOverviewResponse';
import { ClientResponse } from '@/DTO/Clients/ClientResponse';
import { CreateClientRequest } from '@/DTO/Clients/CreateClientRequest';
import { UpdateClientRequest } from '@/DTO/Clients/UpdateClientRequest';
import { useClientsClient } from '@/Hooks/ClientHooks';
import ProcessingState from '@/Models/ProcessingState';
import { resetEmptyStringToNull } from '@/Utils/Helpers';
import PaginationConsts from '@/Utils/PaginationConsts';
import UIStrings from '@/Utils/UIStrings';

interface ClientsOverviewContextType {
    clients: ClientOverviewResponse[];
    isClientsLoading: boolean;
    pagination: Pagination;
    setPagination: React.Dispatch<React.SetStateAction<Pagination>>;

    clientFilters: ClientFilters;
    setClientFilters: React.Dispatch<React.SetStateAction<ClientFilters>>;

    createClient: (newClient: ClientFormValues) => Promise<void>;
    isCreateClientLoading: boolean;

    updateClient: (clientId: string, nextClient: ClientFormValues) => Promise<ClientResponse | undefined>;
    updateClientProcessingState: ProcessingState;

    updateClientIcon: (clientId: string, image: Blob) => Promise<ClientResponse | undefined>;
    resetClientIcon: (clientId: string) => Promise<ClientResponse | undefined>;
    updateClientIconProcessingState: ProcessingState;
}

const ClientsOverviewContext = createContext<ClientsOverviewContextType>(undefined!);

const useMemoContext = <T extends object>(data: T): T => useMemo(() => data, [data]);

export const ClientsOverviewContextProvider = ({ children }: React.PropsWithChildren) => {
    const clientsClient = useClientsClient();
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const [pagination, setPagination] = useState<Pagination>({
        offset: 0,
        limit: PaginationConsts.largePageSize,
        totalCount: 0,
    });
    const [clientFilters, setClientFilters] = useState<ClientFilters>({});
    const {
        data: clients,
        isLoading: isClientsLoading,
        mutate,
    } = useClientsOverview(pagination.offset, pagination.limit, clientFilters.search);
    const { isMutating: isCreateClientLoading, trigger } = useCreateClient();

    useEffect(() => clients?.pagination && setPagination(clients.pagination), [clients?.pagination]);

    const createClient: ClientsOverviewContextType['createClient'] = useCallback(
        async ({ name, websiteURL, linkedInURL }) => {
            const createRequest: CreateClientRequest = {
                name,
                websiteURL: resetEmptyStringToNull(websiteURL),
                linkedInURL: resetEmptyStringToNull(linkedInURL),
            };

            await trigger(createRequest);
            mutate();
        },
        [trigger],
    );

    const [updateClientProcessingState, setUpdateClientProcessingState] = useState<ProcessingState>(
        ProcessingState.Processing,
    );

    // Update details of a non default client, default client should instead propagate
    // to company profile settings values
    const updateClient: ClientsOverviewContextType['updateClient'] = useCallback(
        async (clientId, { name, websiteURL, linkedInURL, description }) => {
            let result;
            setUpdateClientProcessingState(ProcessingState.Processing);
            try {
                const updateRequest: UpdateClientRequest = {
                    name,
                    websiteURL: resetEmptyStringToNull(websiteURL),
                    linkedInURL: resetEmptyStringToNull(linkedInURL),
                    description,
                };

                result = await clientsClient.updateClientAsync(clientId, updateRequest);
                // reload the page to reflect the new client
                mutate();

                setUpdateClientProcessingState(ProcessingState.Success);
                showSuccessToaster(UIStrings.SuccessYourCompanyDetailsHaveBeenUpdated);
            } catch (error) {
                setUpdateClientProcessingState(ProcessingState.Error);
                showFailToaster();
            }

            return result;
        },
        [setUpdateClientProcessingState],
    );

    const [updateClientIconProcessingState, setUpdateClientIconProcessingState] = useState<ProcessingState>(
        ProcessingState.Processing,
    );

    // Update the icon of a non default client, default client should instead
    // update the current company profile settings icon
    const updateClientIcon: ClientsOverviewContextType['updateClientIcon'] = useCallback(
        async (clientId, image) => {
            let result;
            setUpdateClientIconProcessingState(ProcessingState.Processing);
            try {
                result = await clientsClient.updateNonDefaultClientIcon(clientId, image);
                // reload the page to reflect the new client
                mutate();

                setUpdateClientIconProcessingState(ProcessingState.Success);
                showSuccessToaster(UIStrings.SuccessYourCompanyDetailsHaveBeenUpdated);
            } catch (error) {
                setUpdateClientIconProcessingState(ProcessingState.Error);
                showFailToaster();
            }

            return result;
        },
        [setUpdateClientIconProcessingState],
    );

    // Reset the icon of a non default client, default client should instead
    // reset the current company profile settings icon
    const resetClientIcon: ClientsOverviewContextType['resetClientIcon'] = useCallback(
        async (clientId) => {
            let result;
            setUpdateClientIconProcessingState(ProcessingState.Processing);
            try {
                result = await clientsClient.resetNonDefaultClientIcon(clientId);
                // reload the page to reflect the new client
                mutate();

                setUpdateClientIconProcessingState(ProcessingState.Success);
                showSuccessToaster(UIStrings.SuccessYourCompanyDetailsHaveBeenUpdated);
            } catch (error) {
                setUpdateClientIconProcessingState(ProcessingState.Error);
                showFailToaster();
            }

            return result;
        },
        [setUpdateClientIconProcessingState],
    );

    return (
        <ClientsOverviewContext.Provider
            value={useMemoContext<ClientsOverviewContextType>({
                clients: clients?.data || [],
                isClientsLoading,
                pagination,
                setPagination,

                clientFilters,
                setClientFilters,

                createClient,
                isCreateClientLoading,

                updateClient,
                updateClientProcessingState,

                updateClientIcon,
                resetClientIcon,
                updateClientIconProcessingState,
            })}
        >
            {children}
        </ClientsOverviewContext.Provider>
    );
};

export const useClientsOverviewContext = () => {
    const context = useContext(ClientsOverviewContext);

    if (!context) {
        throw new Error('useClientsOverviewContext must be used inside ClientsOverviewContextProvider');
    }

    return context;
};
